import * as Cesium from "cesium";

// Fixer for missing left/right side IDs (Check #2) with STRICT left/right separation
// + Horizontal (ENU) distance/length (ignores height) to avoid Z affecting matching.
export function createMissingSideIdFixer(viewer, currentDataSource, options = {}) {
  const now = options.time || (Cesium?.JulianDate?.now ? Cesium.JulianDate.now() : undefined);

  const maxEndpointDistanceMeters = options.maxEndpointDistanceMeters ?? 4;
  const maxEndpointDistanceSq = maxEndpointDistanceMeters * maxEndpointDistanceMeters;

  const maxLengthDeltaRatio = options.maxLengthDeltaRatio ?? 0.3; // 30%
  const minRightDirectionDot = options.minRightDirectionDot ?? 0.0; // >=0 means not opposite

  const minLateralSeparationMeters = options.minLateralSeparationMeters ?? 0.2;
  const layerKeys = options.layerKeys ?? ["lanemarker", "virtualline", "turninglines", "turningarc"];

  function normalizeForCompare(str) {
    if (str === null || str === undefined) return "";
    return String(str).toLowerCase().replace(/\s+/g, "").replace(/[-_]+/g, "");
  }

  function isAllowedLayerName(layerName) {
    const norm = normalizeForCompare(layerName);
    for (let i = 0; i < layerKeys.length; i++) {
      if (norm.includes(layerKeys[i])) return true;
    }
    return false;
  }

  function getPropertyValueFromProps(props, key) {
    if (!props) return undefined;

    if (typeof props.get === "function") {
      const property = props.get(key);
      if (!property) return undefined;
      return typeof property.getValue === "function" ? property.getValue(now) : property;
    }

    if (props._properties && typeof props._properties === "object") {
      const property = props._properties[key];
      if (!property) return undefined;
      return typeof property.getValue === "function" ? property.getValue(now) : property;
    }

    if (typeof props.getValue === "function") {
      const obj = props.getValue(now);
      if (obj && typeof obj === "object") return obj[key];
    }

    if (Object.prototype.hasOwnProperty.call(props, key)) return props[key];

    return undefined;
  }

  function setPropertyValueOnProps(props, key, value) {
    if (!props) return false;

    if (typeof props.get === "function") {
      const property = props.get(key);
      if (property && typeof property.setValue === "function") {
        property.setValue(value);
        return true;
      }
      if (typeof props.addProperty === "function") {
        props.addProperty(key, value);
        return true;
      }
    }

    if (props._properties && typeof props._properties === "object") {
      const property = props._properties[key];
      if (property && typeof property.setValue === "function") {
        property.setValue(value);
        return true;
      }
      props._properties[key] = value;
      return true;
    }

    props[key] = value;
    return true;
  }

  function getEntityPositions(entity) {
    if (!entity) return null;

    const poly = entity.polyline;
    if (poly) {
      const p = poly.positions ?? poly._positions;
      if (p) {
        if (typeof p.getValue === "function") return p.getValue(now) ?? null;
        if (Array.isArray(p)) return p;
      }
    }

    const direct = entity.positions ?? entity._positions;
    if (direct) {
      if (typeof direct.getValue === "function") return direct.getValue(now) ?? null;
      if (Array.isArray(direct)) return direct;
    }

    const props = entity.properties;
    if (props) {
      const keysToTry = ["positions", "centerline", "center_line", "centerLine", "center_line_positions"];
      for (let i = 0; i < keysToTry.length; i++) {
        const v = getPropertyValueFromProps(props, keysToTry[i]);
        if (Array.isArray(v) && v.length > 1) return v;
      }
    }

    return null;
  }

  function distSq3D(a, b) {
    const dx = a.x - b.x;
    const dy = a.y - b.y;
    const dz = a.z - b.z;
    return dx * dx + dy * dy + dz * dz;
  }

  function dot3(u, v) {
    return u.x * v.x + u.y * v.y + u.z * v.z;
  }

  function directionUnit3D(positions) {
    if (!positions || positions.length < 2) return null;
    const a = positions[0];
    const b = positions[positions.length - 1];

    const v = Cesium.Cartesian3.subtract(b, a, new Cesium.Cartesian3());
    const m = Cesium.Cartesian3.magnitude(v);
    if (!m) return null;

    return { x: v.x / m, y: v.y / m, z: v.z / m };
  }

  function parseReasons(reasons) {
    const arr = Array.isArray(reasons) ? reasons : reasons ? [reasons] : [];
    return {
      needLeft: arr.includes("Left ID not found"),
      needRight: arr.includes("Right ID not found"),
    };
  }

  // Build ENU frame at centerStart and compute 2D (east,north) direction.
  function buildLocalFrame2D(centerPositions) {
    const p0 = centerPositions[0];
    const p1 = centerPositions[centerPositions.length - 1];

    const enu = Cesium.Transforms.eastNorthUpToFixedFrame(p0);
    const invEnu = Cesium.Matrix4.inverse(enu, new Cesium.Matrix4());

    const lp0 = Cesium.Matrix4.multiplyByPoint(invEnu, p0, new Cesium.Cartesian3());
    const lp1 = Cesium.Matrix4.multiplyByPoint(invEnu, p1, new Cesium.Cartesian3());

    const dx = lp1.x - lp0.x; // east
    const dy = lp1.y - lp0.y; // north
    const m = Math.hypot(dx, dy);
    if (!m || !isFinite(m)) return null;

    return {
      invEnu,
      dir2: { x: dx / m, y: dy / m }, // unit in EN plane
      originLocal: lp0,
    };
  }

  function toLocalEN(localFrame2D, worldPoint, out) {
    return Cesium.Matrix4.multiplyByPoint(localFrame2D.invEnu, worldPoint, out || new Cesium.Cartesian3());
  }

  // Horizontal distance squared in EN plane (ignores Up).
  function distSqHorizontal(localFrame2D, aWorld, bWorld) {
    if (!localFrame2D) return distSq3D(aWorld, bWorld);

    const a = toLocalEN(localFrame2D, aWorld);
    const b = toLocalEN(localFrame2D, bWorld);

    const dx = a.x - b.x; // East
    const dy = a.y - b.y; // North
    return dx * dx + dy * dy;
  }

  // Horizontal length along polyline in EN plane (ignores Up).
  function segmentLengthSumHorizontal(localFrame2D, positions) {
    if (!positions || positions.length < 2) return 0;

    if (!localFrame2D) {
      let sum3 = 0;
      for (let i = 1; i < positions.length; i++) sum3 += Cesium.Cartesian3.distance(positions[i - 1], positions[i]);
      return sum3;
    }

    let sum = 0;
    let prev = toLocalEN(localFrame2D, positions[0]);
    for (let i = 1; i < positions.length; i++) {
      const cur = toLocalEN(localFrame2D, positions[i]);
      const dx = cur.x - prev.x;
      const dy = cur.y - prev.y;
      sum += Math.hypot(dx, dy);
      prev = cur;
    }
    return sum;
  }

  function directionUnit2D(localFrame2D, positions) {
    if (!positions || positions.length < 2 || !localFrame2D) return null;

    const a = toLocalEN(localFrame2D, positions[0]);
    const b = toLocalEN(localFrame2D, positions[positions.length - 1]);

    const dx = b.x - a.x;
    const dy = b.y - a.y;
    const m = Math.hypot(dx, dy);
    if (!m || !isFinite(m)) return null;

    return { x: dx / m, y: dy / m };
  }

  function dot2(u, v) {
    return u.x * v.x + u.y * v.y;
  }

  // Signed lateral distance (meters) in EN plane:
  // cross2 = dir.x * vec.y - dir.y * vec.x
  // > 0 => point is LEFT of direction, < 0 => RIGHT
  function signedLateralMeters(localFrame2D, worldPoint) {
    const lp = toLocalEN(localFrame2D, worldPoint);
    const vx = lp.x - localFrame2D.originLocal.x;
    const vy = lp.y - localFrame2D.originLocal.y;
    return localFrame2D.dir2.x * vy - localFrame2D.dir2.y * vx;
  }

  // Precompute candidate lines once.
  const candidates = [];
  const collection = viewer?.dataSources;

  if (collection && typeof collection.length === "number" && typeof collection.get === "function") {
    for (let i = 0; i < collection.length; i++) {
      const ds = collection.get(i);
      if (!ds || ds === currentDataSource || !ds.entities) continue;

      const layerName = ds.layerName;
      if (!layerName || !isAllowedLayerName(layerName)) continue;

      const ents = ds.entities.values || ds.entities;
      if (!Array.isArray(ents) || ents.length === 0) continue;

      for (let j = 0; j < ents.length; j++) {
        const e = ents[j];
        if (!e || !e.properties) continue;

        const idValue = getPropertyValueFromProps(e.properties, "id");
        if (idValue === null || idValue === undefined || String(idValue) === "") continue;

        const positions = getEntityPositions(e);
        if (!positions || positions.length < 2) continue;

        const dir3 = directionUnit3D(positions);
        if (!dir3) continue;

        candidates.push({
          entity: e,
          id: String(idValue),
          positions,
          dir3,
          layerName,
        });
      }
    }
  }

  function chooseCandidateForSide(side, centerPositions, centerLenH, centerDir2, localFrame2D, excludeIdsSet) {
    if (!centerPositions || centerPositions.length < 2 || !centerLenH) return null;

    const centerStart = centerPositions[0];
    const minLen = centerLenH * (1 - maxLengthDeltaRatio);
    const maxLen = centerLenH * (1 + maxLengthDeltaRatio);

    let best = null;
    let bestScore = Infinity;

    for (let i = 0; i < candidates.length; i++) {
      const c = candidates[i];
      if (excludeIdsSet && excludeIdsSet.has(c.id)) continue;

      const p0 = c.positions[0];
      const pN = c.positions[c.positions.length - 1];

      // Horizontal length gate (preferred); fallback to 3D if no local frame.
      const candLenH = segmentLengthSumHorizontal(localFrame2D, c.positions);
      if (!candLenH) continue;
      if (candLenH < minLen || candLenH > maxLen) continue;

      // Direction gate (right only) in horizontal plane if possible.
      if (side === "right") {
        if (localFrame2D && centerDir2) {
          const candDir2 = directionUnit2D(localFrame2D, c.positions);
          if (!candDir2) continue;
          if (dot2(centerDir2, candDir2) < minRightDirectionDot) continue;
        } else {
          // Fallback 3D direction check
          const centerDir3 = directionUnit3D(centerPositions);
          if (!centerDir3) continue;
          if (dot3(centerDir3, c.dir3) < minRightDirectionDot) continue;
        }
      }

      // Choose endpoint used for distance
      let chosenPoint = p0;
      let dSq;

      if (side === "left") {
        const d0 = distSqHorizontal(localFrame2D, centerStart, p0);
        const d1 = distSqHorizontal(localFrame2D, centerStart, pN);
        if (d1 < d0) {
          dSq = d1;
          chosenPoint = pN;
        } else {
          dSq = d0;
          chosenPoint = p0;
        }
      } else {
        dSq = distSqHorizontal(localFrame2D, centerStart, p0);
        chosenPoint = p0;
      }

      if (dSq > maxEndpointDistanceSq) continue;

      // Strict side check to prevent swapping (uses ENU horizontal)
      if (localFrame2D) {
        const lat = signedLateralMeters(localFrame2D, chosenPoint);

        if (side === "left") {
          if (lat < minLateralSeparationMeters) continue;
        } else {
          if (lat > -minLateralSeparationMeters) continue;
        }
      }

      // Score: primary = distance, secondary = |length diff|
      const lenDiff = Math.abs(candLenH - centerLenH);
      const score = dSq + lenDiff * 0.01;

      if (score < bestScore) {
        bestScore = score;
        best = { ...c, lenH: candLenH };
      }
    }

    return best;
  }

  function fixItem(item) {
    const lanelet = item?.entity;
    const props = lanelet?.properties;
    if (!props) return { fixed: false, sides: [], chosen: {} };

    const { needLeft, needRight } = parseReasons(item.reasons);
    if (!needLeft && !needRight) return { fixed: false, sides: [], chosen: {} };

    const centerPositions = getEntityPositions(lanelet);
    if (!centerPositions || centerPositions.length < 2) {
      return { fixed: false, sides: [], chosen: { error: "Centerline positions not found on lanelet entity" } };
    }

    const localFrame2D = buildLocalFrame2D(centerPositions);

    // Use horizontal length in ENU if possible.
    const centerLenH = segmentLengthSumHorizontal(localFrame2D, centerPositions);
    const centerDir2 = localFrame2D ? localFrame2D.dir2 : null;

    if (!centerLenH) {
      return { fixed: false, sides: [], chosen: { error: "Centerline length invalid" } };
    }

    // Avoid picking the same line for both sides, and avoid selecting an already-present side id
    const exclude = new Set();
    const existingLeft = getPropertyValueFromProps(props, "left_line_id");
    const existingRight = getPropertyValueFromProps(props, "right_line_id");
    if (existingLeft !== null && existingLeft !== undefined && String(existingLeft) !== "") exclude.add(String(existingLeft));
    if (existingRight !== null && existingRight !== undefined && String(existingRight) !== "") exclude.add(String(existingRight));

    const sides = [];
    const chosen = {};

    if (needLeft) {
      const leftCand = chooseCandidateForSide("left", centerPositions, centerLenH, centerDir2, localFrame2D, exclude);
      if (leftCand) {
        setPropertyValueOnProps(props, "left_line_id", leftCand.id);
        sides.push("left");
        chosen.left = { id: leftCand.id, layerName: leftCand.layerName };
        exclude.add(leftCand.id);
      }
    }

    if (needRight) {
      const rightCand = chooseCandidateForSide("right", centerPositions, centerLenH, centerDir2, localFrame2D, exclude);
      if (rightCand) {
        setPropertyValueOnProps(props, "right_line_id", rightCand.id);
        sides.push("right");
        chosen.right = { id: rightCand.id, layerName: rightCand.layerName };
        exclude.add(rightCand.id);
      }
    }

    return { fixed: sides.length > 0, sides, chosen };
  }

  function fixAll(items) {
    if (!Array.isArray(items) || items.length === 0) return [];
    const report = new Array(items.length);
    for (let i = 0; i < items.length; i++) report[i] = fixItem(items[i]);
    return report;
  }

  return { fixItem, fixAll, candidatesCount: candidates.length };
}

export function fixMissingSideIds(viewer, currentDataSource, missingItems, options) {
  const fixer = createMissingSideIdFixer(viewer, currentDataSource, options);
  return fixer.fixAll(missingItems);
}
